import React, { useEffect, useState } from "react";
import {
    Plus,
    Trash2,
    RefreshCw,
    ExternalLink,
    Loader2,
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Info,
    X,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";

const TABS = [
    { id: "crawl", label: "Crawl links" },
    { id: "sitemap", label: "Sitemap" },
    { id: "individual", label: "Individual link" },
];

export default function Websites() {
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState("crawl");
    const [scheme, setScheme] = useState("https://");
    const [url, setUrl] = useState("");
    const [adv, setAdv] = useState(false);
    const [excludeMode, setExcludeMode] = useState("Starts with");
    const [excludePath, setExcludePath] = useState("");
    const [excludeRules, setExcludeRules] = useState([]);
    const [slow, setSlow] = useState(false);
    const [includeQuery, setIncludeQuery] = useState(false);
    const [adding, setAdding] = useState(false);

    const load = () =>
        adminApi.get("/api/admin/websites").then((r) => setItems(r.data.items));

    useEffect(() => {
        load();
    }, []);

    const addExcludeRule = () => {
        const path = excludePath.trim();
        if (!path) return;
        setExcludeRules((r) => [...r, { mode: excludeMode, path }]);
        setExcludePath("");
    };

    const fullUrl = () => {
        const u = url.trim();
        if (!u) return "";
        if (u.startsWith("http://") || u.startsWith("https://")) return u;
        return `${scheme}${u}`;
    };

    const submit = async (e) => {
        e?.preventDefault();
        const target = fullUrl();
        if (!target) {
            toast.error("Please enter a URL");
            return;
        }
        setAdding(true);
        try {
            await adminApi.post("/api/admin/websites", { url: target });
            setUrl("");
            toast.success("Page fetched and indexed");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to fetch URL");
        } finally {
            setAdding(false);
        }
    };

    const refetch = async (id) => {
        await adminApi.post(`/api/admin/websites/${id}/refetch`);
        toast.success("Re-indexed");
        load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this source?")) return;
        await adminApi.delete(`/api/admin/websites/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div className="p-10 max-w-5xl" data-testid="page-websites">
            <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-4xl text-stone-900">Website</h1>
                    <p className="text-stone-600 mt-2 max-w-xl text-sm">
                        Crawl web pages or submit sitemaps to update Priya with the latest content from orchidhotel.com or any partner site.
                    </p>
                </div>
                <button
                    className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50"
                    onClick={() =>
                        toast.info(
                            "Crawl crawls all pages from the URL down. Sitemap reads an XML sitemap. Individual link adds a single page only.",
                            { duration: 6000 }
                        )
                    }
                >
                    <Info size={13} /> Learn more
                </button>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl mb-8 overflow-hidden">
                <div className="px-5 py-4 border-b border-stone-200">
                    <div className="font-medium text-stone-900">Add links</div>
                </div>

                {/* Tabs */}
                <div className="px-5 pt-4">
                    <div className="flex border-b border-stone-200">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`web-tab-${t.id}`}
                                className={`px-4 py-2.5 text-sm transition-colors -mb-px border-b-2 ${
                                    tab === t.id
                                        ? "border-stone-900 text-stone-900 font-medium"
                                        : "border-transparent text-stone-500 hover:text-stone-700"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* URL input */}
                <form onSubmit={submit} className="px-5 py-5">
                    <label className="block text-sm text-stone-700 mb-1.5">URL</label>
                    <div className="flex gap-0 mb-3">
                        <select
                            value={scheme}
                            onChange={(e) => setScheme(e.target.value)}
                            data-testid="web-scheme"
                            className="px-3 py-2 border border-stone-200 rounded-l-md text-sm bg-stone-50 text-stone-700 focus:outline-none"
                        >
                            <option value="https://">https://</option>
                            <option value="http://">http://</option>
                        </select>
                        <input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={
                                tab === "sitemap"
                                    ? "www.example.com/sitemap.xml"
                                    : "www.example.com"
                            }
                            data-testid="web-url"
                            className="flex-1 px-3 py-2 border border-l-0 border-stone-200 rounded-r-md text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                    </div>

                    {tab === "crawl" && (
                        <div className="mb-4 bg-stone-50 border border-stone-200 rounded-md px-3 py-2 text-xs text-stone-600 flex items-start gap-2">
                            <Info size={13} className="text-stone-400 mt-0.5 shrink-0" />
                            An exact link count will be shown after training, since new links may be discovered and some links may be invalid.
                        </div>
                    )}

                    {/* Advanced options */}
                    <div className="border-t border-stone-200 mt-4 pt-4">
                        <button
                            type="button"
                            onClick={() => setAdv((a) => !a)}
                            data-testid="web-adv-toggle"
                            className="flex items-center gap-2 text-sm font-medium text-stone-700 hover:text-orchid-700"
                        >
                            {adv ? (
                                <ChevronUp size={16} />
                            ) : (
                                <ChevronDown size={16} />
                            )}
                            Advanced options
                        </button>

                        {adv && (
                            <div className="pt-4 space-y-4">
                                <div>
                                    <label className="block text-sm text-stone-700 mb-1.5">Exclude paths</label>
                                    <div className="flex gap-2">
                                        <select
                                            value={excludeMode}
                                            onChange={(e) => setExcludeMode(e.target.value)}
                                            className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white"
                                        >
                                            <option>Starts with</option>
                                            <option>Contains</option>
                                            <option>Ends with</option>
                                        </select>
                                        <input
                                            value={excludePath}
                                            onChange={(e) => setExcludePath(e.target.value)}
                                            placeholder="/blog"
                                            className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                        />
                                        <button
                                            type="button"
                                            onClick={addExcludeRule}
                                            data-testid="web-add-exclude"
                                            className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    {excludeRules.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {excludeRules.map((r, i) => (
                                                <span
                                                    key={i}
                                                    className="inline-flex items-center gap-1.5 text-xs bg-stone-100 text-stone-700 px-2 py-1 rounded-full"
                                                >
                                                    {r.mode}: {r.path}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setExcludeRules((rules) =>
                                                                rules.filter((_, j) => j !== i)
                                                            )
                                                        }
                                                    >
                                                        <X size={11} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={slow}
                                            onChange={(e) => setSlow(e.target.checked)}
                                            data-testid="web-slow"
                                            className="accent-orchid-700"
                                        />
                                        Slow scraping{" "}
                                        <span title="Use slow scraping to avoid getting rate-limited by the target site.">
                                            <Info size={12} className="text-stone-400" />
                                        </span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={includeQuery}
                                            onChange={(e) => setIncludeQuery(e.target.checked)}
                                            data-testid="web-include-query"
                                            className="accent-orchid-700"
                                        />
                                        Include query parameters{" "}
                                        <span title="Treat ?foo=bar URLs as distinct pages.">
                                            <Info size={12} className="text-stone-400" />
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end mt-5">
                        <button
                            type="submit"
                            disabled={adding || !url.trim()}
                            data-testid="web-fetch"
                            className="text-sm font-medium px-5 py-2 rounded-md bg-stone-900 hover:bg-stone-800 text-white disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {adding ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Plus size={14} />
                            )}
                            {adding ? "Fetching…" : tab === "individual" ? "Add link" : "Fetch links"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Indexed sources table */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-[0.1em]">
                        <tr>
                            <th className="text-left px-5 py-3">URL</th>
                            <th className="text-left px-5 py-3">Chunks</th>
                            <th className="text-left px-5 py-3">Status</th>
                            <th className="text-left px-5 py-3">Indexed</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((s) => (
                            <tr key={s.id} className="border-t border-stone-100">
                                <td className="px-5 py-3">
                                    <a
                                        href={s.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1.5 text-orchid-700 hover:underline truncate max-w-xs"
                                    >
                                        <ExternalLink size={13} /> {s.url}
                                    </a>
                                </td>
                                <td className="px-5 py-3 text-stone-700">{s.chunk_count}</td>
                                <td className="px-5 py-3">
                                    {s.status === "indexed" ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-sage-700">
                                            <CheckCircle2 size={13} /> Indexed
                                        </span>
                                    ) : s.status === "failed" ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-red-700">
                                            <AlertCircle size={13} /> Failed
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-gold-700">
                                            <Loader2 size={13} className="animate-spin" /> {s.status}
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-3 text-stone-600 whitespace-nowrap">
                                    {formatDateTime(s.indexed_at || s.created_at)}
                                </td>
                                <td className="px-5 py-3 flex gap-2">
                                    <button
                                        onClick={() => refetch(s.id)}
                                        className="p-1.5 text-stone-400 hover:text-orchid-700 hover:bg-stone-50 rounded"
                                        title="Re-fetch"
                                    >
                                        <RefreshCw size={14} />
                                    </button>
                                    <button
                                        onClick={() => remove(s.id)}
                                        className="p-1.5 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center text-stone-500 py-12">
                                    No website sources yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
